import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import "./auth.scss";
import {
  sendemailotp,
  sendwhatsappsmsotp,
  verifyemailotp,
  verifywhatsappsmsotp,
  createLoginLog,
} from "../services/api/AuthApi.js";
import { useAuth } from "../context/AuthContext.js";
import gsap from "gsap";

import slide1 from "../assets/images/auth3main.jpg";
import slide2 from "../assets/images/auth3main.jpg";
import slide3 from "../assets/images/auth3main.jpg";
import logodark from "../assets/images/dark-main-pro.jpeg";

import google from "../assets/images/Googlelogo.png";
import facebook from "../assets/images/facebook1.png";
import { getAllGlobalSettingsData } from "../services/api/GlobalSettingsApi.js";
import { useGlobalSettings } from "../context/GlobalSettingsContext.js";
import { getAllBubbleData } from "../services/api/NotificationsApi.js";
import { useBubbleSettings } from "../context/BubbleSettingsContext.js";
import JusticeLoader from "../components/JusticeLoader/JusticeLoader.js";
import { resetLogoutFlag } from "../services/api/axios.js";

const fetchIPAddress = async () => {
  try {
    const response = await fetch("https://api.ipify.org?format=json");
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data = await response.json();
    // console.log("IP Address fetched:", data.ip);
    return data.ip;
  } catch (error) {
    console.error("Error fetching IP address:", error);
    return "0.0.0.0";
  }
};

export const Login = () => {
  const { user, accessToken, login, company } = useAuth();
  const [otpRequestLoading, setOtpRequestLoading] = useState(false);
  const [verifyBtnLoading, setVerifyBtnLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [screenSize, setScreenSize] = useState("desktop");
  const [isLoadingUserData, setIsLoadingUserData] = useState(false);

  const { setGlobalSettings } = useGlobalSettings();
  const { setBubbleSettings } = useBubbleSettings();

  const navigate = useNavigate();

  // Function to check if subscription is expired
  const isSubscriptionExpired = useCallback((company) => {
    if (!company?.subscription?.currentPeriodEnd) return false;

    const endDate = new Date(company.subscription.currentPeriodEnd);
    const currentDate = new Date();
    
    return currentDate > endDate;
  }, []);

  const [error, setError] = useState("");
  const [mode, setMode] = useState("mobile");
  const [contact, setContact] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [timer, setTimer] = useState(0);
  const otpRefs = useRef([]);

  const fetchAndSetUserData = useCallback(async () => {
    try {
      setIsLoadingUserData(true);

      const settingsRes = await getAllGlobalSettingsData();

      if (settingsRes?.data && typeof settingsRes.data === "object") {
        localStorage.setItem(
          "globalSettings",
          JSON.stringify(settingsRes.data)
        );
        setGlobalSettings(settingsRes.data);
      } else {
        console.warn("Invalid global settings response:", settingsRes);
      }

      const bubbleRes = await getAllBubbleData();

      if (bubbleRes?.data && typeof bubbleRes.data === "object") {
        localStorage.setItem("bubbleSettings", JSON.stringify(bubbleRes.data));
        setBubbleSettings(bubbleRes.data);
      } else {
        console.warn("Invalid bubble settings response:", bubbleRes);
      }
    } catch (error) {
      console.error("Error fetching user data:", error);

      localStorage.removeItem("globalSettings");
      localStorage.removeItem("bubbleSettings");

      setGlobalSettings(null);
      setBubbleSettings(null);
    } finally {
      setIsLoadingUserData(false);
    }
  }, []);

  useEffect(() => {
  let isMounted = true;
  
  const handleAuthenticatedUser = async () => {
    if (user && accessToken && isMounted) {
      console.log("User already authenticated, fetching latest data...");
      await fetchAndSetUserData();
      
      if (isSubscriptionExpired(company)) {
        console.log("Subscription expired, setting flag for modal display");
        localStorage.setItem('showSubscriptionExpiredModal', 'true');
      }
      
      navigate("/home", { replace: true });
    }
  };

  handleAuthenticatedUser();
  
  return () => {
    isMounted = false;
  };
}, []);

  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      setIsMobile(width <= 768);

      if (width <= 480) {
        setScreenSize("mobile");
      } else if (width <= 768) {
        setScreenSize("tablet");
      } else if (width <= 1024) {
        setScreenSize("desktop-small");
      } else {
        setScreenSize("desktop");
      }
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);

    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  useEffect(() => {
    if (user && accessToken) return;

    const title = document.querySelector(".form-title");
    if (title) {
      const text = title.textContent;
      title.style.opacity = "0";
      title.innerHTML = "";

      text.split("").forEach((char, index) => {
        const span = document.createElement("span");
        span.textContent = char === " " ? "\u00A0" : char;
        span.style.opacity = "0";
        span.style.display = "inline-block";
        title.appendChild(span);
      });

      title.style.opacity = "1";

      const delay = isMobile ? 0.03 : 0.05;

      title.querySelectorAll("span").forEach((span, index) => {
        gsap.to(span, {
          opacity: 1,
          duration: 0.3,
          delay: index * delay,
          ease: "power2.out",
        });
      });
    }
  }, [isMobile, user, accessToken]);

  useEffect(() => {
    if (otpSent && timer > 0) {
      const countdown = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(countdown);
    }
  }, [otpSent, timer]);

  const handleContactChange = (e) => {
    setContact(e.target.value);
    setError("");
  };

  const handleEditContact = () => {
    setOtpSent(false);
    setOtp(["", "", "", "", "", ""]);
    setTimer(0);
    setError("");
    // Focus on the input field after a short delay
    setTimeout(() => {
      document.querySelector('.input-field')?.focus();
    }, 100);
  };

  const handleOtpChange = (e, index) => {
    const value = e.target.value.replace(/\D/g, "");

    if (value.length > 1) {
      const digits = value.slice(0, 6);
      const newOtp = Array(6).fill("");

      for (let i = 0; i < digits.length && i < 6; i++) {
        newOtp[i] = digits[i];
      }

      setOtp(newOtp);

      const nextIndex = Math.min(digits.length - 1, 5);
      setTimeout(() => {
        otpRefs.current[nextIndex]?.focus();
      }, 0);

      return;
    }

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);


    if (value) {
      gsap.fromTo(
        otpRefs.current[index],
        { scale: 0.9 },
        {
          scale: 1,
          duration: isMobile ? 0.15 : 0.2,
          ease: "back.out(1.7)",
        }
      );
    }

    if (index < otp.length - 1 && value) {
      setTimeout(() => {
        otpRefs.current[index + 1]?.focus();
      }, 0);
    }

    if (value) {
      const isComplete = newOtp.every((digit) => digit !== "");


      if (isComplete) {
        setTimeout(() => {

          if (newOtp.every((digit) => digit !== "" && digit.length === 1)) {
            handleVerifyOtpWithData(newOtp);
          }
        }, 500);
      }
    }
  };

  const handleOtpKeyDown = (e, index) => {
    if (e.key === "Backspace") {
      e.preventDefault();
      const newOtp = [...otp];

      if (newOtp[index]) {
        newOtp[index] = "";
        setOtp(newOtp);
      } else if (index > 0) {
        newOtp[index - 1] = "";
        setOtp(newOtp);
        otpRefs.current[index - 1]?.focus();
      }
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text");
    const digits = pastedData.replace(/\D/g, "").slice(0, 6);

    if (digits.length > 0) {
      const newOtp = Array(6).fill("");
      digits.split("").forEach((digit, index) => {
        if (index < 6) newOtp[index] = digit;
      });
      setOtp(newOtp);

      const nextIndex = Math.min(digits.length, 5);
      otpRefs.current[nextIndex]?.focus();
    }
  };

  const validateContact = () => {
    if (mode === "email") {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(contact);
    } else {
      const phoneRegex = /^[6-9]\d{9}$/;
      return phoneRegex.test(contact);
    }
  };

  const handleSendOtp = async () => {
    if (!contact.trim()) {
      setError(
        `Please enter your ${
          mode === "email" ? "email address" : "mobile number"
        }`
      );
      return;
    }

    if (!validateContact()) {
      setError(
        mode === "email"
          ? "Please enter a valid email address"
          : "Please enter a valid 10-digit mobile number"
      );
      return;
    }

    setOtpRequestLoading(true);
    setError("");

    try {
      let response;

      if (mode === "email") {
        response = await sendemailotp({ email: contact });
      } else if (mode === "mobile") {
        response = await sendwhatsappsmsotp({ phone: contact });
      }

      if (response?.data?.error == 'User not found') {
        setError("User Not Found");
        setOtpSent(false);
        setOtp(["", "", "", "", "", ""]);
        setTimer(0);
        return;
      }

      if (response?.data?.userActiveStatus === false || response?.data?.companyActiveStatus === false) {
        setError(response.data.message || "Your account is deactivated. Please contact support.");
        setOtpSent(false);
        setOtp(["", "", "", "", "", ""]);
        setTimer(0);
        return;
      }

      setOtpSent(true);
      setTimer(60);
      setOtp(["", "", "", "", "", ""]);
      setError("");

      setTimeout(() => {
        otpRefs.current[0]?.focus();
      }, 100);

    } catch (err) {
      if (
        err.response?.data?.userActiveStatus === false ||
        err.response?.data?.companyActiveStatus === false
      ) {
        setError(err.response.data.message || "Your account is deactivated. Please contact support.");
        setOtpSent(false);
        setOtp(["", "", "", "", "", ""]);
        setTimer(0);
      } else if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError("Failed to send OTP. Please try again.",err);
      }
    } finally {
      setOtpRequestLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    await handleVerifyOtpWithData();
  };

const handleVerifyOtpWithData = async (otpData = null) => {

  const currentOtp = otpData || [...otp];
  const enteredOtp = currentOtp.join("");

  if (enteredOtp.length !== 6) {
    setError("Please enter the complete 6-digit OTP");
    return;
  }

  const userIP = await fetchIPAddress();
  // console.log("User IP for logging:", userIP);

  try {
    setVerifyBtnLoading(true);
    setError("");
    let res;

    if (mode === "email") {
      res = await verifyemailotp({ email: contact, otp: enteredOtp });
    } else if (mode === "mobile") {
      res = await verifywhatsappsmsotp({ phone: contact, otp: enteredOtp });
    }

    const { accessToken, company, name, role, visibility, email } = res.data;

    // Reset the logout flag to allow future invalid token handling
    resetLogoutFlag();

    login({ name, [mode]: contact, email }, accessToken, company, role, visibility);

    try {
      const logData = {
        ip: userIP || "0.0.0.0",
        type: mode === "email" ? "email" : "whatsapp", 
        status: "success",
        companyId: company?._id || null,
        userId: res.data?.userId || null,
      };
      
      const logResponse = await createLoginLog(logData);
    } catch (logError) {
      console.error("Error creating SUCCESS login log:", logError);
      console.error("Log error details:", logError.response?.data || logError.message);
    }

    await fetchAndSetUserData();
    
    navigate("/home");
    
  } catch (err) {
    console.log("API Error:", err);
    
    try {
      const logData = {
        ip: userIP || "0.0.0.0",
        type: mode === "email" ? "email" : "whatsapp",
        status: "failed"
      };
      
      const logResponse = await createLoginLog(logData);
    } catch (logError) {
      console.error("Error creating FAILED login log:", logError);
    }
    
    setError("Invalid OTP. Please try again.");
    const logData = {
      ip: userIP || "0.0.0.0",
      type: mode === "email" ? "email" : "whatsapp", 
      status: "failed"
    };
    
    await createLoginLog(logData);
    setOtp(["", "", "", "", "", ""]);
    setTimeout(() => {
      otpRefs.current[0]?.focus();
    }, 100);
  } finally {
    setVerifyBtnLoading(false);
  }
};

  
  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (otpSent) {
        handleVerifyOtp();
      } else {
        handleSendOtp();
      }
    }
  };

  if ((user && accessToken) || isLoadingUserData) {
    return (
      <div className="global-loading-overlay">
        <JusticeLoader
          message={isLoadingUserData ? "Loading user data..." : "Loading..."}
          type="gavel"
        />
      </div>
    );
  }

  return (
    <div className="login-container" onKeyPress={handleKeyPress}>
      <div className="image-section">
        <div className="image-overlay"></div>
      </div>

      <div className="form-section">
        <div className="main-logo-log">
          <img
            src={logodark}
            alt="Company Logo"
            className="logo-dark-main-login"
            onClick={() => window.open("http://lawsuit-pro.com", "_self")}
            loading="eager"
          />
        </div>

        <div className="auth-form-content">
          {/* <h1 className="form-title">Welcome Back!</h1> */}
          <div className="forgot-password">
            <span className="forgot-password-text">Don't have an account?</span>
            <a
              href="http://lawsuit-pro.com/pricing.html"
              className="forgot-password-link"
            >
              <u>Create a new account now!</u>
            </a>
          </div>

          <div className="mode-tabs">
            <button
              className={`mode-tab ${mode === "mobile" ? "active" : ""}`}
              onClick={() => {
                setMode("mobile");
                setContact("");
                setError("");
                setOtpSent(false);
                setOtp(["", "", "", "", "", ""]);
                setTimer(0);
              }}
            >
              MOBILE
            </button>
            <button
              className={`mode-tab ${mode === "email" ? "active" : ""}`}
              onClick={() => {
                setMode("email");
                setContact("");
                setError("");
                setOtpSent(false);
                setOtp(["", "", "", "", "", ""]);
                setTimer(0);
              }}
            >
              EMAIL
            </button>
          </div>

          <form onSubmit={(e) => e.preventDefault()} className="auth-form">
            <div className="input-section">
              <label className="input-label">
                <span className="label-text">
                  {mode === "mobile" ? "Mobile Number" : "Email"}
                </span>
              </label>
              <div className="input-container">
                {mode === "mobile" && (
                  <div className="country-code">
                    <span className="flag">🇮🇳</span>
                    <span>+91</span>
                  </div>
                )}
                <input
                  type={mode === "email" ? "email" : "tel"}
                  placeholder={
                    mode === "email"
                      ? "Enter your email"
                      : "Enter your mobile number"
                  }
                  className="input-field"
                  value={contact}
                  onChange={handleContactChange}
                  required
                  autoComplete={mode === "email" ? "email" : "tel"}
                  inputMode={mode === "email" ? "email" : "numeric"}
                  maxLength={mode === "email" ? undefined : 10}
                  disabled={otpSent}
                />
                {otpSent && (
                  <button
                    type="button"
                    className="edit-contact-btn"
                    onClick={handleEditContact}
                    title="Edit contact"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                    </svg>
                  </button>
                )}
                {otpSent && timer > 0 && (
                  <button type="button" className="resend-otp-btn" disabled>
                    Resend ({timer}s)
                  </button>
                )}
                {otpSent && timer === 0 && (
                  <button
                    type="button"
                    className="resend-otp-btn"
                    onClick={handleSendOtp}
                    disabled={otpRequestLoading}
                  >
                    {otpRequestLoading ? "Sending..." : "Resend OTP"}
                  </button>
                )}
              </div>
            </div>

            {otpSent && (
              <div className="otp-input-section">
                <label className="input-label">
                  <span className="label-text">Enter OTP</span>
                </label>
                <div className="otp-boxes">
                  {otp.map((digit, idx) => (
                    <input
                      key={idx}
                      type="text"
                      maxLength="1"
                      className="otp-input"
                      value={digit}
                      onChange={(e) => handleOtpChange(e, idx)}
                      onKeyDown={(e) => handleOtpKeyDown(e, idx)}
                      onPaste={idx === 0 ? handleOtpPaste : undefined}
                      ref={(el) => (otpRefs.current[idx] = el)}
                      inputMode="numeric"
                      pattern="[0-9]*"
                      autoComplete="one-time-code"
                    />
                  ))}
                </div>
              </div>
            )}

            {error && (
              <div className="error-message" role="alert">
                {error}
              </div>
            )}

            <button
              type="button"
              className="send-otp-btn"
              onClick={otpSent ? handleVerifyOtp : handleSendOtp}
              disabled={
                (!otpSent && !contact.trim()) ||
                otpRequestLoading ||
                verifyBtnLoading
              }
            >
              {otpSent ? (
                verifyBtnLoading ? (
                  <>
                    <span>Verifying</span>
                    {isMobile && <span>...</span>}
                  </>
                ) : (
                  "LOGIN"
                )
              ) : otpRequestLoading ? (
                <>
                  <span>Sending</span>
                  {isMobile && <span>...</span>}
                </>
              ) : (
                "Send OTP"
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};